import React, { useCallback, useEffect, useState } from 'react';
import { adminWrite, adminWriteLive, fetchStaffCustomerPick } from '../services/api';

/**
 * Shared pieces of the Tools & Lists screens moved from the admin portal (Azure exit WS8).
 * Reads work from the sql01 copy. Save controls stay disabled until the door says the screen's
 * ws8_<screen>_live flag is on, and that flag is the cutover.
 */

export function useWriteLive(feature) {
  const [live, setLive] = useState(false);
  useEffect(() => {
    let on = true;
    adminWriteLive(feature).then((v) => { if (on) setLive(v); }).catch(() => {});
    return () => { on = false; };
  }, [feature]);
  return live;
}

// The last load wins, so a slow answer to an old filter cannot replace a newer one.
export function useRows(load, deps) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let on = true;
    setRows(null);
    setError(null);
    load()
      .then((r) => { if (on) setRows(r); })
      .catch((e) => { if (on) setError(e.message); });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { rows, error, reload: useCallback(() => setTick((t) => t + 1), []) };
}

// Runs one door action, keeps its refusal to show, and reloads the list when it saved.
export function useSave(reload) {
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = useCallback(async (action, body) => {
    setSaving(true);
    setError(null);
    try {
      const out = await adminWrite(action, body);
      reload();
      return out || true;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  }, [reload]);
  return { save, saving, error, setError };
}

export function WriteOffNote({ live, what = 'Add, edit and delete' }) {
  if (live) return null;
  return <div className="staff-write-off">Reading only. {what} still happen in the admin portal until cutover.</div>;
}

export function PageTitle({ children }) {
  return (
    <div className="page-header">
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>{children}</h2>
    </div>
  );
}

export function ListCard({ title, rows, error, empty = 'Nothing to show.', children }) {
  return (
    <div className="card">
      <div className="card-header">{title}{rows ? ` (${rows.length})` : ''}</div>
      {error && <div className="staff-msg staff-msg-error">Error: {error}</div>}
      {!rows && !error && <div className="staff-msg">Loading...</div>}
      {rows && rows.length === 0 && <div className="staff-msg">{empty}</div>}
      {rows && rows.length > 0 && <div className="port-scroll">{children}</div>}
    </div>
  );
}

export function EditDialog({ title, onClose, onSave, live, saving, error, saveLabel = 'Save', children }) {
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {children}
          {error && <div className="staff-msg staff-msg-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!live || saving} onClick={onSave}>{saveLabel}</button>
        </div>
      </div>
    </div>
  );
}

// The admin portal customer picker: any of the four fields, part of the value is enough.
export function CustomerPicker({ onPick }) {
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', phoneNumber: '' });
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const search = () => { setError(null); fetchStaffCustomerPick(f).then(setRows).catch((e) => setError(e.message)); };
  const box = (key, label) => <Field label={label}><input className="form-input" value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} /></Field>;
  return (
    <div>
      <div className="staff-grid">{box('firstName', 'First Name')}{box('lastName', 'Last Name')}{box('email', 'Email')}{box('phoneNumber', 'Phone')}</div>
      <button type="button" className="btn btn-outline" onClick={search}>Search Customers</button>
      {error && <div className="staff-msg staff-msg-error">{error}</div>}
      {rows && rows.length === 0 && <div className="staff-msg">No customers found.</div>}
      {rows && rows.length > 0 && (
        <div className="port-scroll">
          <table className="trans-table">
            <tbody>
              {rows.map((c) => (
                <tr key={c.contactId} style={{ cursor: 'pointer' }} onClick={() => onPick(c)}>
                  <td>{c.firstName} {c.lastName}</td><td>{c.email}</td><td>{c.phone}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}